/**
 * Graine de l'ordre ALÉATOIRE de la galerie (Accueil).
 *
 * L'ordre aléatoire est PAR UTILISATEUR : la graine est PERSISTÉE côté serveur
 * (`CurrentUser.randomSeed`) → l'ordre est IDENTIQUE et STABLE sur toutes les
 * machines/sessions du compte, re-mélangé UNIQUEMENT à la demande via
 * `POST /api/auth/random-seed` (bouton « Re-mélanger »). Elle ne voyage PLUS dans l'URL
 * depuis le LOT C1 (`?graine=` retiré) : chaque écran la relit du compte via
 * `accountRandomSeed`, ce qui suffit à garder pagination et aller-retour visionneuse
 * COHÉRENTS (sinon chaque page se re-mélangerait).
 *
 * `generateSeed()` n'est plus qu'un REPLI DÉFENSIF, utilisé quand la graine serveur
 * manque (contrat `randomSeed` optionnel côté SPA), pour ne jamais planter. Il n'y a
 * pas de graine CLIENT persistée (session/`localStorage`) ni de « mode ».
 *
 * Format : token alphanumérique + `-`/`_` (URL-safe), 12 caractères — sous-ensemble de
 * `ASSET_SEED_PATTERN` (≤ 64) validé côté API.
 */

/** Longueur de la graine générée (12 chars alphanumériques URL-safe). */
const SEED_LENGTH = 12;

/** Alphabet URL-safe (sous-ensemble de `ASSET_SEED_PATTERN`). */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** Génère une graine aléatoire URL-safe de `SEED_LENGTH` caractères. */
export function generateSeed(): string {
  const n = ALPHABET.length;
  // `crypto.getRandomValues` si dispo (meilleure entropie), sinon `Math.random`.
  const cryptoObj =
    typeof globalThis !== 'undefined' && 'crypto' in globalThis
      ? (globalThis.crypto as Crypto | undefined)
      : undefined;
  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(SEED_LENGTH);
    cryptoObj.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < SEED_LENGTH; i += 1) out += ALPHABET[bytes[i]! % n];
    return out;
  }
  let out = '';
  for (let i = 0; i < SEED_LENGTH; i += 1) out += ALPHABET[Math.floor(Math.random() * n)];
  return out;
}

/**
 * Graine de REPLI, générée une seule fois et mémoïsée AU NIVEAU DU MODULE.
 *
 * Elle doit être PARTAGÉE : la grille (`GalleryView`) et la visionneuse (`ViewerRoute`)
 * résolvent chacune leur ordre de leur côté et, la graine n'étant plus portée par l'URL,
 * deux replis indépendants (une `ref` par composant) leur donneraient deux ordres
 * DIFFÉRENTS — préc./suiv. sauterait à côté. Une valeur de module tient toute la durée de
 * vie de l'onglet, ce qui suffit : ce repli ne sert que le temps qu'une graine serveur
 * existe.
 */
let fallbackSeed: string | null = null;

/**
 * Graine d'aléatoire EFFECTIVE du compte : celle du serveur si elle existe, sinon le
 * repli défensif partagé ci-dessus. Jamais vide (une graine vide dégénérerait côté API en
 * `md5('' || id)` : ordre figé, identique pour tous et non re-mélangeable).
 */
export function accountRandomSeed(serverSeed: string | null | undefined): string {
  if (typeof serverSeed === 'string' && serverSeed.length > 0) return serverSeed;
  if (fallbackSeed === null) fallbackSeed = generateSeed();
  return fallbackSeed;
}
